import { useEffect, useRef } from 'react';
import type {
  ApolloError,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';

/**
 * The shape of a relay-style connection field returned by a `findMany*` query.
 */
type PageConnection = {
  edges: ReadonlyArray<unknown>;
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
};

/**
 * The minimal variable shape shared by all paginated `findMany*` queries.
 * Used to inject the `after` cursor when fetching subsequent pages.
 */
type PaginatedVariables = {
  input?: {
    pagination?: { first?: number | null; after?: string | null } | null;
  } | null;
};

/**
 * A generated Apollo query hook (e.g. `useFindManyAttributeLocalesQuery`).
 */
type GeneratedQueryHook<TData, TVariables> = (
  baseOptions?: QueryHookOptions<TData, TVariables>,
) => QueryResult<TData, TVariables>;

/**
 * Fetches *all* pages of a paginated `findMany*` query.
 *
 * Apollo paginates the result into pages of `pagination.first` items. This hook
 * issues the initial query and then keeps calling `fetchMore` (merging each
 * page's edges into the cache) until `pageInfo.hasNextPage` is false.
 *
 * `loading` stays true while there are still pages left to fetch, so consumers
 * can treat the connection as complete once `loading` becomes false.
 *
 * @param useQuery       The generated Apollo query hook to drive.
 * @param connectionKey  The data field holding the connection (e.g. `findManyAttributeLocales`).
 * @param variables      Variables for the first page. Should include `pagination.first`.
 * @param skip           Skip the query entirely.
 */
export function useFetchAllPages<
  TKey extends string,
  TData extends Record<TKey, PageConnection>,
  TVariables extends PaginatedVariables,
>({
  useQuery,
  connectionKey,
  variables,
  skip = false,
}: {
  useQuery: GeneratedQueryHook<TData, TVariables>;
  connectionKey: TKey;
  variables: TVariables;
  skip?: boolean;
}): {
  data: TData | undefined;
  loading: boolean;
  error: ApolloError | undefined;
} {
  const { data, loading, error, fetchMore } = useQuery({ skip, variables });

  // Keep the latest variables in a ref so the pagination effect doesn't re-run
  // (and re-trigger fetchMore) just because the caller rebuilt them inline.
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const pageInfo = data?.[connectionKey].pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor;

  useEffect(() => {
    if (skip || !hasNextPage || !endCursor) return;

    const current = variablesRef.current;
    const nextVariables = {
      ...current,
      input: {
        ...current.input,
        pagination: {
          ...current.input?.pagination,
          after: endCursor,
        },
      },
    } as TVariables;

    void fetchMore({
      variables: nextVariables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const prevConn = (prev as Record<TKey, PageConnection>)[connectionKey];
        const nextConn = (fetchMoreResult as Record<TKey, PageConnection>)[
          connectionKey
        ];
        return {
          ...fetchMoreResult,
          [connectionKey]: {
            ...nextConn,
            edges: [...prevConn.edges, ...nextConn.edges],
          },
        } as typeof prev;
      },
    });
  }, [skip, hasNextPage, endCursor, fetchMore, connectionKey]);

  return { data, loading: loading || hasNextPage, error };
}
